import { AlertTriangle, ChevronDown, MessageSquare, Trash2 } from "lucide-react";
import { DateTime } from "luxon";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useWorkers } from "@/domains/workers/hooks/use-workers";
import type { AppointmentListFragment, AppointmentServiceFragment } from "@/gql/graphql";
import { useConfig } from "@/hooks/use-config";
import { useCurrentAbility } from "@/lib/authorization";
import { timezoneUtils } from "@/lib/timezone";
import { ColorUtils } from "@/lib/utils/color-utils";
import { AppointmentOutcomeDropdown } from "./appointment-outcome-dropdown";
import { AppointmentStatusDropdown } from "./appointment-status-dropdown";

interface DesktopAppointmentTableProps {
  appointments: AppointmentListFragment[];
  currentDate: DateTime;
  multiServiceExpanded: boolean;
  onAppointmentClick?: (appointmentId: string) => void;
  onStatusUpdate?: (
    appointmentId: string,
    newBookingStatus: string,
    newOutcome?: string | null
  ) => void;
  onOutcomeUpdate?: (appointmentId: string, newOutcome: string | null) => void;
  onServiceWorkerUpdate?: (
    appointmentId: string,
    serviceId: string,
    workerId: string | null
  ) => void;
  onDeleteAppointment?: (appointmentId: string) => void;
}

interface AppointmentRowProps {
  appointment: AppointmentListFragment;
  currentDate: DateTime;
  showWorker?: boolean;
  multiServiceExpanded: boolean;
  onWorkerUpdate: (appointmentId: string, serviceId: string, workerId: string) => void;
  onAppointmentClick?: (appointmentId: string) => void;
  onStatusUpdate?: (
    appointmentId: string,
    newBookingStatus: string,
    newOutcome?: string | null
  ) => void;
  onOutcomeUpdate?: (appointmentId: string, newOutcome: string | null) => void;
  onDeleteAppointment?: (appointmentId: string) => void;
}

function AppointmentRow({
  appointment,
  showWorker = true,
  multiServiceExpanded,
  onWorkerUpdate,
  onAppointmentClick,
  onStatusUpdate,
  onOutcomeUpdate,
  onDeleteAppointment,
}: AppointmentRowProps) {
  const { t } = useTranslation("newCalendar");
  const { t: tAppt } = useTranslation("appointments");
  const _ability = useCurrentAbility();
  const canDelete = false; // TODO: enable when delete appointment feature is ready
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Use actual appointment data
  const appt = {
    id: appointment.id,
    startTime: timezoneUtils.fromISO(appointment.startTime),
    endTime: timezoneUtils.fromISO(appointment.endTime),
    customer: {
      name:
        `${appointment.customer?.firstName || ""} ${appointment.customer?.lastName || ""}`.trim() ||
        t("common.unknownCustomer"),
      phone: appointment.customer?.primaryPhone?.phone || t("common.noPhone"),
      email: appointment.customer?.email || t("common.noEmail"),
    },
    services: appointment.appointmentServices?.map((service) => ({
      name: service.serviceName || t("common.unknownService"),
    })) || [{ name: t("common.unknownService") }],
    worker: {
      name: t("common.unassigned"), // AppointmentListFragment doesn't have a direct worker field - workers are on services
    },
    status: appointment.bookingStatus || "PENDING",
    outcome: appointment.outcome || null,
    notes: appointment.notes || "",
  };

  // Check if appointment has multiple services
  const isMultipleServices = (appointment.appointmentServices?.length || 0) > 1;

  // Clean appointment editing logic with proper timezone handling
  const now = DateTime.now(); // Uses the globally set salon timezone from TimezoneService
  const isPast = appt.startTime < now;
  const isToday = appt.startTime.hasSame(now, "day");

  // Confirmation page logic - prioritize booking status for pending appointments
  // 1. PENDING appointments (any time) -> status editing (need confirmation first)
  // 2. Past CONFIRMED appointments -> outcome editing
  // 3. Today's CONFIRMED appointments -> outcome editing
  // 4. Future CONFIRMED appointments -> status editing (can still be modified)

  const shouldShowOutcome = appt.status === "CONFIRMED" && (isPast || isToday);

  // Function to render worker cell based on expansion state and assignments
  const renderWorkerCell = (
    appointment: AppointmentListFragment,
    isMultipleServices: boolean,
    multiServiceExpanded: boolean
  ) => {
    // For multiple services in expanded mode, hide content (will be shown in sub-rows)
    if (isMultipleServices && multiServiceExpanded) {
      return <div />;
    }

    // For single service or multiple services in collapsed mode
    const services = appointment.appointmentServices || [];

    if (services.length === 0) {
      return (
        <span className="text-sm text-muted-foreground">
          {t("table.workerAssignment.unassigned")}
        </span>
      );
    }

    if (services.length === 1) {
      // Single service - show worker name or "Unassigned" with color indicator
      const service = services[0];
      const workerName =
        service.worker?.displayName ||
        (service.worker?.user
          ? `${service.worker.user.firstName} ${service.worker.user.lastName || ""}`
          : t("table.workerAssignment.unassigned"));

      const _colorStyle = service.worker?.color
        ? ColorUtils.getColorStyle(service.worker.color)
        : null;

      return (
        <div className="flex items-center gap-2">
          {/* dot hidden intentionally */}
          <span
            className={service.worker ? "font-medium text-sm" : "text-sm text-muted-foreground"}
          >
            {workerName}
          </span>
        </div>
      );
    }

    // Multiple services in collapsed mode
    const assignedServices = services.filter((service) => service.worker);
    const allAssigned = assignedServices.length === services.length;

    if (!allAssigned) {
      // Show attention icon if not all services are assigned
      return (
        <div className="flex items-center gap-1">
          <AlertTriangle className="h-4 w-4 text-orange-500" />
          <span className="text-sm text-muted-foreground">
            {t("table.workerAssignment.incomplete")}
          </span>
        </div>
      );
    }

    // All services assigned - show unique worker names with colors
    const uniqueWorkers = new Map();
    services.forEach((service) => {
      if (service.worker?.user?.firstName) {
        const name =
          service.worker.displayName ||
          `${service.worker.user.firstName} ${service.worker.user.lastName || ""}`;
        const color = service.worker.color;
        if (!uniqueWorkers.has(name)) {
          uniqueWorkers.set(name, color);
        }
      }
    });

    if (uniqueWorkers.size === 1) {
      // Single unique worker - show with color
      const [workerName, workerColor] = Array.from(uniqueWorkers.entries())[0];
      const _colorStyle = workerColor ? ColorUtils.getColorStyle(workerColor) : null;

      return (
        <div className="flex items-center gap-2">
          {/* dot hidden intentionally */}
          <span className="font-medium text-sm">{workerName}</span>
        </div>
      );
    }
    // Multiple workers - remove dot indicators and show a simple label
    return (
      <div className="flex items-center gap-1">
        <span className="font-medium text-xs text-muted-foreground">
          {t("table.workerAssignment.multiple")}
        </span>
      </div>
    );
  };

  // Function to render services cell based on expansion state
  const renderServicesCell = (
    appointment: AppointmentListFragment,
    isMultipleServices: boolean,
    multiServiceExpanded: boolean
  ) => {
    // For multiple services in collapsed mode, show service labels in single line
    if (isMultipleServices && !multiServiceExpanded) {
      const services =
        appointment.appointmentServices
          ?.slice()
          .sort((a, b) => a.sequenceOrder - b.sequenceOrder) || [];

      const serviceNames = services.map((service) => service.serviceName).join(", ");

      return (
        <div className="truncate text-sm text-muted-foreground" title={serviceNames}>
          <span>{serviceNames}</span>
        </div>
      );
    }

    // For multiple services in expanded mode, hide content (will be shown in sub-rows)
    if (isMultipleServices && multiServiceExpanded) {
      return <div />;
    }

    // For single service, show the service in the main row
    const service = appointment.appointmentServices?.[0];
    return (
      <div className="text-sm text-muted-foreground">
        {service ? <span>{service.serviceName}</span> : <span>{t("common.unknownService")}</span>}
      </div>
    );
  };

  return (
    <>
      <TableRow
        className="hover:bg-muted/50 h-12 cursor-pointer transition-colors"
        onClick={() => onAppointmentClick?.(appt.id)}
      >
        <TableCell className="py-2 w-20">
          <div className="flex items-center gap-2">
            <div className="text-sm text-muted-foreground">
              <span>{appt.startTime.toFormat("HH:mm")}</span>
            </div>
            {appointment.customerMessage && (
              <MessageSquare className="w-4 h-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
            )}
          </div>
        </TableCell>

        <TableCell className="py-2 w-48">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm truncate">{appt.customer.name}</span>
            {appointment.customer?.totalVisits === 0 && (
              <Badge
                variant="secondary"
                className="text-xs px-1.5 py-0 h-5 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 flex-shrink-0"
              >
                {t("common.newCustomer")}
              </Badge>
            )}
          </div>
        </TableCell>

        <TableCell className="py-2 w-64">
          {renderServicesCell(appointment, isMultipleServices, multiServiceExpanded)}
        </TableCell>

        {showWorker && (
          <TableCell className="py-2 w-40">
            {renderWorkerCell(appointment, isMultipleServices, multiServiceExpanded)}
          </TableCell>
        )}

        <TableCell className="py-2 w-36" onClick={(e) => e.stopPropagation()}>
          {shouldShowOutcome ? (
            <AppointmentOutcomeDropdown
              appointmentId={appt.id}
              currentOutcome={appt.outcome}
              onOutcomeChange={(newOutcome) => onOutcomeUpdate?.(appt.id, newOutcome)}
            />
          ) : (
            <AppointmentStatusDropdown
              appointmentId={appt.id}
              currentStatus={appt.status}
              onStatusChange={(newStatus) => onStatusUpdate?.(appt.id, newStatus)}
            />
          )}
        </TableCell>

        <TableCell className="py-2 w-14">
          <Badge variant="outline" className="text-xs">
            {tAppt(`paymentMethod_${appointment.paymentMethod ?? "NONE"}`)}
          </Badge>
        </TableCell>

        {canDelete && (
          <TableCell className="py-2 w-16" onClick={(e) => e.stopPropagation()}>
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete appointment?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete the appointment for{" "}
                    <span className="font-medium">{appt.customer.name}</span>. This action cannot be
                    undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={() => onDeleteAppointment?.(appt.id)}
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </TableCell>
        )}
      </TableRow>

      {/* Only render sub-rows for multiple services when expanded */}
      {isMultipleServices &&
        multiServiceExpanded &&
        appointment.appointmentServices
          ?.slice()
          .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
          .map((service, index, array) => (
            <AppointmentServiceSubRow
              key={service.id}
              appointmentService={service}
              appointmentId={appointment.id}
              isLast={index === array.length - 1}
              showWorker={showWorker}
              onWorkerUpdate={onWorkerUpdate}
            />
          ))}
    </>
  );
}

interface AppointmentServiceSubRowProps {
  appointmentService: AppointmentServiceFragment;
  appointmentId: string;
  isLast?: boolean;
  showWorker?: boolean;
  onWorkerUpdate?: (appointmentId: string, serviceId: string, workerId: string) => void;
}

function AppointmentServiceSubRow({
  appointmentService,
  appointmentId,
  isLast = false,
  showWorker = true,
  onWorkerUpdate,
}: AppointmentServiceSubRowProps) {
  const { t } = useTranslation("newCalendar");
  const { workers } = useWorkers({
    filters: { isActive: true },
  });
  const ability = useCurrentAbility();
  const canUpdate = ability.can("update", "Appointment");

  const workerName =
    appointmentService.worker?.displayName ||
    (appointmentService.worker?.user
      ? `${appointmentService.worker.user.firstName} ${appointmentService.worker.user.lastName || ""}`
      : t("table.workerAssignment.unassigned"));

  const _colorStyle = appointmentService.worker?.color
    ? ColorUtils.getColorStyle(appointmentService.worker.color)
    : null;

  return (
    <TableRow
      className={`hover:bg-muted/10 transition-colors border-l-4 border-l-primary/20 ${isLast ? "border-b border-border" : ""}`}
    >
      {/* Empty Time Column with connector */}
      <TableCell className="py-1 px-2 relative w-20">
        <div className={`absolute left-4 top-0 w-px bg-border ${isLast ? "h-1/2" : "bottom-0"}`} />
        <div className="absolute left-4 top-1/2 right-0 h-px bg-border" />
      </TableCell>

      {/* Empty Customer Column */}
      <TableCell className="py-1 px-2 bg-muted/50 w-48" />

      {/* Service Column */}
      <TableCell className="py-1 px-2 text-sm text-muted-foreground relative w-64">
        <div className="flex items-center gap-2">
          {/* dot hidden intentionally */}
          <span className="truncate">{appointmentService.serviceName}</span>
        </div>
      </TableCell>

      {/* Worker Column with dropdown */}
      {showWorker && (
        <TableCell className="py-1 px-2 text-sm w-40">
          {!canUpdate ? (
            <div className="flex items-center gap-2 w-fit">
              {/* dot hidden intentionally */}
              <span
                className={appointmentService.worker ? "text-xs" : "text-xs text-muted-foreground"}
              >
                {workerName}
              </span>
            </div>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 gap-1 border border-border hover:bg-accent justify-start"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* dot hidden intentionally */}
                  <span
                    className={
                      appointmentService.worker ? "text-xs" : "text-xs text-muted-foreground"
                    }
                  >
                    {workerName}
                  </span>
                  <ChevronDown className="w-3 h-3 ml-auto" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                {workers.map((worker) => {
                  const _workerColorStyle = ColorUtils.getColorStyle(worker.color);
                  return (
                    <DropdownMenuItem
                      key={worker.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        onWorkerUpdate?.(appointmentId, appointmentService.id, worker.id);
                      }}
                      className="flex items-center gap-2"
                    >
                      {/* dot hidden intentionally */}
                      <span>
                        {worker.displayName ||
                          (worker.user
                            ? `${worker.user.firstName} ${worker.user.lastName}`
                            : "Unknown Worker")}
                      </span>
                    </DropdownMenuItem>
                  );
                })}
                {appointmentService.worker && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        onWorkerUpdate?.(appointmentId, appointmentService.id, "unassigned");
                      }}
                      className="text-red-600"
                    >
                      {t("table.workerAssignment.removeAssignment")}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </TableCell>
      )}

      {/* Empty Status Column */}
      <TableCell className="py-1 px-2 bg-muted/50 w-36" />
      {/* Empty Payment Column */}
      <TableCell className="py-1 px-2 bg-muted/50 w-14" />
      {/* Empty Delete Column */}
      <TableCell className="py-1 px-2 bg-muted/50 w-16" />
    </TableRow>
  );
}

export function DesktopAppointmentTable({
  appointments,
  currentDate,
  multiServiceExpanded,
  onAppointmentClick,
  onStatusUpdate,
  onOutcomeUpdate,
  onServiceWorkerUpdate,
  onDeleteAppointment,
}: DesktopAppointmentTableProps) {
  const { t } = useTranslation("newCalendar");
  const { value: workerAssignmentEnabled = false } = useConfig(
    "salon.appointment_worker_assignment_enabled"
  );

  // TODO: Implement proper worker assignment update functionality
  const handleWorkerUpdate = (_appointmentId: string, _serviceId: string, _workerId: string) => {
    // This would need to call a GraphQL mutation to update the worker assignment
    // For now, we'll just log it
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-sm overflow-hidden">
      <Table className="table-fixed">
        {/* Table Headers */}
        <TableHeader>
          <TableRow className="bg-muted border-b border-border hover:bg-muted">
            <TableHead className="w-20 p-3 text-sm font-medium text-foreground border-r border-border">
              {t("table.headers.time")}
            </TableHead>
            <TableHead className="w-48 p-3 text-sm font-medium text-foreground border-r border-border">
              {t("table.headers.customer")}
            </TableHead>
            <TableHead className="w-64 p-3 text-sm font-medium text-foreground border-r border-border">
              {t("table.headers.services")}
            </TableHead>
            {workerAssignmentEnabled && (
              <TableHead className="w-40 p-3 text-sm font-medium text-foreground border-r border-border">
                {t("table.headers.worker")}
              </TableHead>
            )}
            <TableHead className="w-36 p-3 text-sm font-medium text-foreground border-r border-border">
              {t("table.headers.status")}
            </TableHead>
            <TableHead className="w-14 p-3 text-sm font-medium text-foreground border-r border-border">
              {t("table.headers.payment")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {appointments.map((appointment) => (
            <AppointmentRow
              key={appointment.id}
              appointment={appointment}
              currentDate={currentDate}
              showWorker={workerAssignmentEnabled}
              multiServiceExpanded={multiServiceExpanded}
              onWorkerUpdate={onServiceWorkerUpdate || handleWorkerUpdate}
              onAppointmentClick={onAppointmentClick}
              onStatusUpdate={onStatusUpdate}
              onOutcomeUpdate={onOutcomeUpdate}
              onDeleteAppointment={onDeleteAppointment}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
